import { ChangeEvent, useState } from 'react';
import React from 'react';
import { NON_DIGIT_CHARS } from '@access-cms/shared-constants';

import Dropdown from '../../../../components/Dropdown';
import { Drawer } from '../../../../components/Drawer';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { WorkflowSubStepProps } from '../../config';
import { email, phoneNumber, required, zipCode } from '../../utils/fieldValidation';

import './WorkContactInformationPage.css';
import { formatUsPhoneNumber, normalizeUsPhoneNumber } from '@/utils';
import { STATE_AND_TERRITORY_OPTIONS } from '@/utils/states';

export interface WorkContactInformationFormData {
  workEmail: string;
  workPhoneNumber: string;
  workExtension: string;
  workAddressLine1: string;
  workAddressLine2: string;
  workAddressCity: string;
  workAddressState: string;
  workAddressZipCode: string;
}

function WorkContactInformationPage({
  data,
  updateData,
  fieldErrors,
}: WorkflowSubStepProps<WorkContactInformationFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'work-contact-information-page';

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      { field: 'workEmail', label: 'Work Email', validationRules: [required, email] },
      { field: 'workPhoneNumber', label: 'Work Phone Number', validationRules: [required, phoneNumber] },
      { field: 'workAddressLine1', label: 'Work Address Line 1', validationRules: [required] },
      { field: 'workAddressCity', label: 'Work Address City', validationRules: [required] },
      { field: 'workAddressState', label: 'Work Address State', validationRules: [required] },
      { field: 'workAddressZipCode', label: 'Work Address Zip Code', validationRules: [required, zipCode] },
    ],
  });

  const handleChange = (field: keyof WorkContactInformationFormData, value: string) => {
    updateData({
      ...data,
      [field]: value,
    });
    handleValidationChange(field, value);
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPos = input.selectionStart || 0;
    const value = input.value;

    if (e.key === 'Backspace' && cursorPos > 0) {
      const charBeforeCursor = value[cursorPos - 1];
      if (
        charBeforeCursor === '(' ||
        charBeforeCursor === ')' ||
        charBeforeCursor === ' ' ||
        charBeforeCursor === '-'
      ) {
        e.preventDefault();
        const digitsOnly = data.workPhoneNumber || '';
        const newDigits = digitsOnly.slice(0, -1);
        updateData({
          ...data,
          workPhoneNumber: newDigits,
        });
      }
    }
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const limitedDigits = normalizeUsPhoneNumber(input).slice(0, 10);

    updateData({
      ...data,
      workPhoneNumber: limitedDigits,
    });
    handleValidationChange('workPhoneNumber', limitedDigits);
  };

  const handleZipCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const digitsOnly = input.replace(NON_DIGIT_CHARS, '');
    const limitedDigits = digitsOnly.slice(0, 9);

    let formattedZip = limitedDigits;
    if (limitedDigits.length > 5) {
      formattedZip = `${limitedDigits.slice(0, 5)}-${limitedDigits.slice(5)}`;
    }

    updateData({
      ...data,
      workAddressZipCode: formattedZip,
    });
    handleValidationChange('workAddressZipCode', formattedZip);
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='work-contact-info__field-container ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-work-email`}>
        <TextField
          required
          autoComplete='email'
          data-testid={`${baseTestId}-work-email-field`}
          errorMessage={getFieldError('workEmail')}
          label='Work Email'
          name='workEmail'
          tooltipText='Your official company or organization email address. This will be used for work-related communications about your CMS access.'
          type='email'
          value={data.workEmail || ''}
          onBlur={() => handleBlur('workEmail')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('workEmail', e.target.value)}
        />
      </div>

      <div
        className='work-contact-info__phone-row ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-phone-row`}>
        <div data-testid={`${baseTestId}-work-phone`}>
          <TextField
            required
            autoComplete='tel'
            data-testid={`${baseTestId}-work-phone-field`}
            errorMessage={getFieldError('workPhoneNumber')}
            hint='(XXX) XXX-XXXX'
            label='Work Phone Number'
            name='workPhoneNumber'
            tooltipText='Your work phone number where you can be reached during business hours. This should be a direct line or desk phone at your workplace.'
            type='tel'
            value={formatUsPhoneNumber(data.workPhoneNumber || '')}
            onBlur={() => handleBlur('workPhoneNumber')}
            onChange={handlePhoneChange}
            onKeyDown={handlePhoneKeyDown}
          />
        </div>
        <div data-testid={`${baseTestId}-work-extension`}>
          <TextField
            data-testid={`${baseTestId}-work-extension-field`}
            label='Work Extension'
            name='workExtension'
            tooltipText='Your phone extension number if required to reach you directly. Leave blank if not applicable.'
            value={data.workExtension || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('workExtension', e.target.value)}
          />
        </div>
      </div>

      <div
        className='work-contact-info__field-container ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-address-line-1`}>
        <TextField
          required
          autoComplete='address-line1'
          data-testid={`${baseTestId}-address-line-1-field`}
          errorMessage={getFieldError('workAddressLine1')}
          label='Work Address Line 1'
          name='workAddressLine1'
          tooltipText='Your workplace street address. This should be the physical location where you primarily work.'
          value={data.workAddressLine1 || ''}
          onBlur={() => handleBlur('workAddressLine1')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('workAddressLine1', e.target.value)}
        />
      </div>

      <div
        className='work-contact-info__field-container ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-address-line-2`}>
        <TextField
          autoComplete='address-line2'
          data-testid={`${baseTestId}-address-line-2-field`}
          label='Work Address Line 2'
          name='workAddressLine2'
          tooltipText='Additional address information such as suite number, floor, or building number. Leave blank if not applicable.'
          value={data.workAddressLine2 || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('workAddressLine2', e.target.value)}
        />
      </div>

      <div
        className='work-contact-info__field-container ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-city`}>
        <TextField
          required
          autoComplete='address-level2'
          data-testid={`${baseTestId}-city-field`}
          errorMessage={getFieldError('workAddressCity')}
          label='Work Address City'
          name='workAddressCity'
          tooltipText='The city where your workplace is located.'
          value={data.workAddressCity || ''}
          onBlur={() => handleBlur('workAddressCity')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('workAddressCity', e.target.value)}
        />
      </div>

      <div
        className='work-contact-info__address-row ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-state-zip`}>
        <div data-testid={`${baseTestId}-state`}>
          <Dropdown
            required
            searchable
            autoComplete='address-level1'
            data-testid={`${baseTestId}-state-dropdown`}
            errorMessage={getFieldError('workAddressState')}
            hint='Select your state or territory'
            label='Work Address State/Territory'
            name='workAddressState'
            options={STATE_AND_TERRITORY_OPTIONS}
            tooltipText='The state or territory where your workplace is located.'
            value={data.workAddressState || ''}
            onBlur={() => handleBlur('workAddressState')}
            onChange={(value: string) => handleChange('workAddressState', value)}
          />
        </div>
        <div data-testid={`${baseTestId}-zip`}>
          <TextField
            required
            autoComplete='postal-code'
            data-testid={`${baseTestId}-zip-field`}
            errorMessage={getFieldError('workAddressZipCode')}
            label='Work Address Zip Code'
            name='workAddressZipCode'
            tooltipText='The ZIP code for your workplace address. Can be 5 digits (12345) or 9 digits (12345-6789).'
            type='text'
            value={data.workAddressZipCode || ''}
            onBlur={() => handleBlur('workAddressZipCode')}
            onChange={handleZipCodeChange}
          />
        </div>
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Work Contact Information'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Work Contact Details</h3>
          <p>
            Please provide your work contact information. This is so we can reach you at your workplace about your
            application and CMS system access.
          </p>

          <h4 className='ds-u-margin-top--3'>Work Email</h4>
          <p>
            Enter your official work email address. Do not use a personal email. This address will be used to contact
            you about work matters and your CMS access.
          </p>

          <h4 className='ds-u-margin-top--3'>Work Phone Number</h4>
          <p>
            Enter your work phone number in the format (XXX) XXX-XXXX. This should be a direct line or desk phone where
            you can be reached during business hours (9 AM - 5 PM ET).
          </p>

          <h4 className='ds-u-margin-top--3'>Work Extension</h4>
          <p>
            If your work phone number requires an extension to reach you directly, enter it here. Leave this field blank
            if not applicable.
          </p>

          <h4 className='ds-u-margin-top--3'>Work Address</h4>
          <p>
            Enter your work address. This should be the location where you primarily work and where you can be reached
            if needed. This address should be the same as the one listed in your job contract or employment records.
          </p>
          <ul className='ds-u-margin-top--2'>
            <li>
              <strong>Address Line 1:</strong> Enter your workplace&apos;s street address.
            </li>
            <li>
              <strong>Address Line 2:</strong> Enter your workplace&apos;s suite, floor, or building number (if this
              applies).
            </li>
            <li>
              <strong>City:</strong> Enter the city name of your workplace.
            </li>
            <li>
              <strong>State:</strong> Select your workplace&apos;s state from the dropdown.
            </li>
            <li>
              <strong>Zip Code:</strong> Enter your workplace&apos;s 5-digit ZIP code.
            </li>
          </ul>
        </div>
      </Drawer>
    </div>
  );
}

export default WorkContactInformationPage;
